<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';

import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useCreateImovelState } from '../useCreateImovelState';
import { COMPLETION_HANDOFF_CHECKLIST } from '../constants';
import StepMedia from '../components/StepMedia.vue';
import StepDetails from '../components/StepDetails.vue';
import StepReview from '../components/StepReview.vue';

const router = useRouter();
const route = useRoute();
const accountId = useMapGetter('getCurrentAccountId');
const controller = useCreateImovelState(accountId);

const deepLinkPrefilled = ref(false);
const completionChecklist = COMPLETION_HANDOFF_CHECKLIST;

const STEPS = [
  { id: 1, label: 'Mídia', icon: 'i-lucide-image' },
  { id: 2, label: 'Detalhes', icon: 'i-lucide-list' },
  { id: 3, label: 'Revisão', icon: 'i-lucide-check-circle' },
];

const currentStep = computed(() => controller.step.value);

const stepComponent = computed(() => {
  if (currentStep.value === 1) return StepMedia;
  if (currentStep.value === 2) return StepDetails;
  return StepReview;
});

watch(
  () => route.query,
  query => {
    deepLinkPrefilled.value = controller.applyDeepLinkPrefill(query);
  },
  { immediate: true }
);

onMounted(() => {
  controller.loadProfiles();
});

const onNext = () => {
  controller.nextStep();
};

const goToImoveis = () => {
  router.push(frontendURL(`accounts/${accountId.value}/imoveis`));
};

const goToCompletionChecklist = imovel => {
  const id = imovel?.id;
  if (!id) {
    goToImoveis();
    return;
  }

  router.push({
    name: 'imovel_show',
    params: {
      accountId: accountId.value,
      id,
    },
    query: {
      from: 'criar_imovel',
      checklist: 'publicabilidade',
    },
  });
};

const onPublish = async () => {
  const created = await controller.publish();
  if (created) {
    useAlert('Imóvel criado. Abrindo checklist de completude.', null, {
      variant: 'success',
    });
    goToCompletionChecklist(created);
  } else if (controller.errorMessage.value) {
    useAlert(controller.errorMessage.value, null, { variant: 'error' });
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex h-full w-full flex-col overflow-y-auto bg-lais-bg">
    <LaisHeader
      title="Criar imóvel"
      subtitle="Wizard simplificado para criação rápida. O editor completo fica em Imóveis e concentra estimativa, taxas, acessos e chaves."
    />

    <div
      class="border-b border-lais-border-soft bg-white px-4 py-3 sm:px-6 lg:px-10"
    >
      <div
        class="mx-auto flex w-full max-w-3xl flex-col gap-3 rounded-lais border border-lais-primary/20 bg-lais-bg-soft p-4 text-[13px] leading-relaxed text-lais-text-body"
      >
        <p class="font-semibold text-lais-text">
          Criação canônica simplificada: Criar imóvel
        </p>
        <p>
          Este fluxo cria o anúncio inicial com o mesmo normalizador de payload.
          Ao concluir, você vai para o detalhe com o checklist de completude;
          estimativa QPreço, taxas e acessos/chaves ficam no editor completo.
        </p>
        <div
          class="grid gap-2 border-t border-lais-border-soft pt-3 sm:grid-cols-2"
        >
          <div
            v-for="item in completionChecklist"
            :key="item.id"
            class="flex items-center gap-2 text-[12px] font-medium text-lais-text-body"
          >
            <span
              class="i-lucide-circle-check size-4 text-lais-primary"
              aria-hidden="true"
            />
            {{ item.label }}
          </div>
        </div>
        <p
          v-if="deepLinkPrefilled"
          class="rounded-lais-sm border border-lais-primary/20 bg-white px-3 py-2 text-[12px] font-medium text-lais-primary"
        >
          Dados mínimos pré-preenchidos pelo link.
        </p>
      </div>
    </div>

    <!-- Stepper -->
    <div
      class="border-b border-lais-border-soft bg-white px-4 py-4 sm:px-6 lg:px-10"
    >
      <div class="mx-auto flex w-full max-w-3xl items-center justify-between">
        <template v-for="(s, index) in STEPS" :key="s.id">
          <button
            type="button"
            class="flex items-center gap-2"
            :disabled="s.id > currentStep"
            @click="s.id < currentStep && controller.goToStep(s.id)"
          >
            <span
              class="flex size-8 items-center justify-center rounded-full text-[13px] font-semibold transition-colors"
              :class="
                s.id === currentStep
                  ? 'bg-lais-primary text-white'
                  : s.id < currentStep
                    ? 'bg-lais-primary-50 text-lais-primary'
                    : 'bg-lais-bg-soft text-lais-text-muted'
              "
            >
              <span
                v-if="s.id < currentStep"
                class="i-lucide-check size-4"
                aria-hidden="true"
              />
              <template v-else>{{ s.id }}</template>
            </span>
            <span
              class="hidden text-[13px] font-medium sm:inline"
              :class="
                s.id === currentStep
                  ? 'text-lais-text-body'
                  : 'text-lais-text-muted'
              "
            >
              {{ s.label }}
            </span>
          </button>
          <div
            v-if="index < STEPS.length - 1"
            class="mx-2 h-px flex-1"
            :class="s.id < currentStep ? 'bg-lais-primary' : 'bg-lais-border'"
          />
        </template>
      </div>
    </div>

    <!-- Conteúdo do passo -->
    <div class="flex-1 px-4 py-6 sm:px-6 lg:px-10">
      <component :is="stepComponent" :controller="controller" />
    </div>

    <!-- Erro -->
    <div v-if="controller.errorMessage.value" class="px-4 sm:px-6 lg:px-10">
      <div
        class="mx-auto flex w-full max-w-3xl items-center gap-2 rounded-lais border border-lais-danger/30 bg-lais-danger/5 px-4 py-3 text-[13px] text-lais-danger"
      >
        <span
          class="i-lucide-alert-circle size-4 shrink-0"
          aria-hidden="true"
        />
        {{ controller.errorMessage.value }}
      </div>
    </div>

    <!-- Barra de ações fixa -->
    <div
      class="sticky bottom-0 z-10 border-t border-lais-border-soft bg-white px-4 py-3 sm:px-6 lg:px-10"
    >
      <div
        class="mx-auto flex w-full max-w-3xl items-center justify-between gap-3"
      >
        <LaisPrimaryButton
          v-if="currentStep > 1"
          variant="ghost"
          icon="i-lucide-arrow-left"
          @click="controller.prevStep"
        >
          Voltar
        </LaisPrimaryButton>
        <span v-else />

        <LaisPrimaryButton
          v-if="currentStep < 3"
          icon="i-lucide-arrow-right"
          @click="onNext"
        >
          Continuar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-else
          icon="i-lucide-rocket"
          :is-loading="controller.isPublishing.value"
          @click="onPublish"
        >
          Criar e abrir checklist
        </LaisPrimaryButton>
      </div>
    </div>
  </div>
</template>
